import { useState } from 'react';
import { Group, Popover, Progress, Text, UnstyledButton } from '@mantine/core';
import { FiChevronDown } from 'react-icons/fi';
import { t } from 'i18next';
import { usePluginProgress, usePluginRows } from '@morph-mapper/plugins';
import { useStore } from '@/store';
import {
  PluginProgressRows,
  isRowReady,
  overallProgress,
} from '../../../common/features/plugin-progress';

/**
 * The progress of a re-evaluation, shown in the header while the plugins
 * bootstrap again: one small bar with the latest message. Clicking it opens
 * the same per-plugin rows as the setup's Loading Plugins step, in a
 * dropdown, so the header never pushes the page down.
 */
export const BootstrapProgress = () => {
  const rebootstrapInProgress = useStore((s) => s.ai.rebootstrapInProgress);
  const entries = usePluginProgress();
  const rows = usePluginRows();
  const [opened, setOpened] = useState(false);

  if (!rebootstrapInProgress) return null;

  const overall = overallProgress(rows) ?? 0;
  const ready = rows.filter(isRowReady).length;
  const latest = entries.length
    ? entries.reduce((a, b) => (b.time > a.time ? b : a))
    : undefined;

  return (
    <Popover
      opened={opened}
      onChange={setOpened}
      position="bottom"
      width={420}
      shadow="md"
      withinPortal
    >
      <Popover.Target>
        <UnstyledButton
          onClick={() => setOpened((o) => !o)}
          aria-expanded={opened}
          aria-haspopup="dialog"
          aria-label={t('plugins.summary', { ready, total: rows.length })}
          sx={{ width: '100%', maxWidth: 320, borderRadius: 6 }}
        >
          <Group spacing={6} noWrap>
            <Progress
              value={overall}
              size="sm"
              radius="xl"
              animate={overall < 100}
              sx={{ flex: 1 }}
            />
            <FiChevronDown
              size={14}
              style={{
                flexShrink: 0,
                transform: opened ? 'rotate(180deg)' : undefined,
                transition: 'transform 150ms ease',
              }}
            />
          </Group>
          {latest?.message && (
            <Text fz="xs" c="dimmed" ta="center" mt={2} truncate>
              {latest.message}
            </Text>
          )}
        </UnstyledButton>
      </Popover.Target>
      <Popover.Dropdown>
        <Text fz="sm" fw={500} mb="sm">
          {t('plugins.summary', { ready, total: rows.length })}
        </Text>
        <PluginProgressRows rows={rows} />
      </Popover.Dropdown>
    </Popover>
  );
};
